import { Link } from "react-router-dom";
import type { ProductCard } from "@itaas/pep-contracts";
import type { PepLocale } from "../../i18n/types";

interface ProductPersonaCardsProps {
  useCases: string[];
  locale: PepLocale;
  title?: string;
}

function splitUseCase(text: string): { title: string; body: string } {
  const sep = text.indexOf(" — ");
  if (sep === -1) return { title: text, body: "" };
  return {
    title: text.slice(0, sep).trim(),
    body: text.slice(sep + 3).trim(),
  };
}

export function ProductPersonaCards({ useCases, locale, title }: ProductPersonaCardsProps) {
  if (!useCases.length) return null;

  return (
    <section className="mb-5">
      <h2 className="h4 fw-bold mb-4">
        {title ?? (locale === "en" ? "Built for your facility" : "Conçu pour votre établissement")}
      </h2>
      <div className="row g-3">
        {useCases.map((uc) => {
          const { title: cardTitle, body } = splitUseCase(uc);
          return (
            <div className="col-md-6" key={uc}>
              <div className="pep-product-persona h-100">
                <h3 className="h6 fw-bold mb-2">{cardTitle}</h3>
                {body ? <p className="text-secondary small mb-0">{body}</p> : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

interface ProductVerticalCrossLinksProps {
  product: ProductCard;
  locale: PepLocale;
}

export function ProductVerticalCrossLinks({ product, locale }: ProductVerticalCrossLinksProps) {
  const crossLinks: Record<string, Array<{ to: string; label: string }>> = {
    edu: [
      { to: "/industries/education", label: locale === "en" ? "Education sector" : "Secteur Éducation" },
      { to: "/solutions/vertical-education", label: locale === "en" ? "Education solution" : "Solution Éducation" },
      { to: "/affiliation/inscription?product=edu", label: locale === "en" ? "Ambassador program" : "Programme ambassadeur" },
    ],
    hospitol: [
      { to: "/industries/healthcare", label: locale === "en" ? "Healthcare sector" : "Secteur Santé" },
      { to: "/solutions/vertical-healthcare", label: locale === "en" ? "Healthcare solution" : "Solution Santé" },
    ],
    insurance: [
      { to: "/industries/healthcare", label: locale === "en" ? "Healthcare sector" : "Secteur Santé" },
      { to: "/solutions/vertical-healthcare", label: locale === "en" ? "Healthcare solution" : "Solution Santé" },
      { to: "/products/hospitol", label: locale === "en" ? "Hospitol hospital platform" : "Plateforme Hospitol" },
    ],
    mineportal: [
      { to: "/industries/rdc-mining", label: locale === "en" ? "Mining in DRC" : "Mines RDC" },
      { to: "/solutions/vertical-mining", label: locale === "en" ? "Mining solution" : "Solution Mines" },
      { to: "/products/subportal", label: locale === "en" ? "Subcontractor portal" : "Portail sous-traitants" },
    ],
    subportal: [
      { to: "/industries/rdc-mining", label: locale === "en" ? "Mining in DRC" : "Mines RDC" },
      { to: "/solutions/vertical-mining", label: locale === "en" ? "Mining solution" : "Solution Mines" },
      { to: "/products/mineportal", label: locale === "en" ? "Mining Company Portal" : "Mining Company Portal" },
    ],
    comex: [
      { to: "/solutions/vertical-logistics", label: locale === "en" ? "Transit & customs solution" : "Solution Transit & douane" },
      { to: "/products/mineportal", label: locale === "en" ? "Mining inbound (MRO)" : "Inbound minier (MRO)" },
    ],
    security: [
      { to: "/industries/security-services", label: locale === "en" ? "Private security sector" : "Secteur Sécurité privée" },
      { to: "/solutions/security-operations", label: locale === "en" ? "Security operations solution" : "Solution Sécurité privée" },
    ],
    legal: [
      { to: "/industries/professional-services", label: locale === "en" ? "Professional services" : "Services professionnels" },
    ],
  };

  const links = crossLinks[product.slug] ?? [];

  if (!links.length) return null;

  return (
    <section className="mb-5">
      <h2 className="h6 text-secondary text-uppercase mb-3">
        {locale === "en" ? "Explore further" : "Aller plus loin"}
      </h2>
      <div className="d-flex flex-wrap gap-2">
        {links.map((link) => (
          <Link key={link.to} className="btn btn-outline-secondary btn-sm" to={link.to}>
            {link.label} →
          </Link>
        ))}
      </div>
    </section>
  );
}

interface ProductVerticalHeroProps {
  product: ProductCard;
  locale: PepLocale;
}

export function ProductVerticalHeroExtras({ product, locale }: ProductVerticalHeroProps) {
  const kicker =
    locale === "en" && product.heroKickerEn
      ? product.heroKickerEn
      : product.heroKickerFr;

  return kicker ? (
    <p className="text-uppercase small fw-semibold mb-2 pep-hero-kicker opacity-90">{kicker}</p>
  ) : null;
}
